<template>
  <div ref="wrapperEl" class="relative w-full">
    <!-- Input row -->
    <div class="relative flex items-center">
      <!-- Left: search icon -->
      <span class="absolute left-3 flex items-center pointer-events-none" style="color: var(--color-muted);">
        <MdiMagnify class="w-4 h-4" />
      </span>

      <!-- Input -->
      <input
        ref="inputEl"
        :aria-label="label || placeholder"
        :role="results !== null ? 'combobox' : undefined"
        :aria-expanded="results !== null ? dropdownVisible : undefined"
        :aria-controls="dropdownVisible && options.length ? `${id}-list` : undefined"
        :aria-activedescendant="dropdownVisible && options[highlightIdx] ? `${id}-option-${highlightIdx}` : undefined"
        :aria-autocomplete="results !== null ? 'list' : undefined"
        :aria-invalid="error ? true : undefined"
        :aria-describedby="error ? `${id}-error` : undefined"
        :aria-busy="loading"
        :value="query"
        :placeholder="placeholder"
        autocomplete="off"
        autocorrect="off"
        autocapitalize="off"
        spellcheck="false"
        inputmode="text"
        class="!pl-9 !pr-24"
        :class="{ 'border-red-500/50 !shadow-none': !!error }"
        @input="onInput"
        @focus="onFocus"
        @blur="onBlur"
        @keydown="onKeydown"
      />

      <!-- Right: loading, camera, clear -->
      <div class="absolute right-2 inset-y-0 flex items-center gap-1 pointer-events-none">
        <span v-if="loading" class="flex items-center pointer-events-none" style="color: var(--color-muted);">
          <MdiLoading class="w-4 h-4 animate-spin" />
        </span>
        <button
          v-if="cameraMode"
          type="button"
          class="pointer-events-auto flex items-center justify-center w-9 h-9 rounded transition-colors"
          :style="cameraOpen ? 'color: var(--color-accent);' : 'color: var(--color-muted);'"
          :title="$t('search.cameraScan')"
          @mousedown.prevent
          @click="toggleCamera"
        >
          <MdiQrcodeScan class="w-4 h-4" />
        </button>
        <button
          v-if="query"
          type="button"
          :aria-label="$t('common.clear')"
          class="pointer-events-auto flex items-center justify-center w-9 h-9 rounded transition-colors hover:text-white"
          style="color: var(--color-muted);"
          @mousedown.prevent
          @click="clear"
        >
          <MdiClose class="w-3.5 h-3.5" />
        </button>
      </div>
    </div>

    <!-- Error -->
    <p v-if="error" :id="`${id}-error`" role="alert" class="text-xs mt-1 px-1 text-[var(--color-danger-text)]">{{ error }}</p>

    <!-- Dropdown -->
    <div
      v-if="dropdownVisible"
      class="absolute left-0 right-0 top-full mt-1 rounded-lg overflow-hidden shadow-xl z-50"
      style="background: var(--color-surface-2); border: 1px solid var(--color-border);"
    >
      <!-- Results -->
      <template v-if="results && results.length">
        <div
          class="flex items-center justify-between px-3 py-1.5 text-xs"
          style="border-bottom: 1px solid var(--color-border); color: var(--color-muted);"
        >
          <span>{{ $t('search.results') }}</span>
          <span class="badge badge-muted">{{ results.length }}</span>
        </div>
        <ul :id="`${id}-list`" role="listbox" :aria-label="$t('search.results')">
          <li
            v-for="(result, i) in results"
            :key="result.id ?? i"
            :id="`${id}-option-${i}`" role="option" :aria-selected="i === highlightIdx"
            class="flex items-center gap-2 px-3 py-2.5 cursor-pointer transition-colors"
            :class="i === highlightIdx ? 'bg-[var(--color-surface)]' : 'hover:bg-[var(--color-surface)]'"
            @mousedown.prevent
            @click="selectResult(result)"
          >
            <span class="flex-1 min-w-0">
              <!-- eslint-disable-next-line vue/no-v-html -->
              <span class="block text-sm font-medium truncate" v-html="highlightMatch(result.label)" />
              <span
                v-if="result.sublabel"
                class="block text-xs truncate mt-0.5"
                style="color: var(--color-muted);"
              >{{ result.sublabel }}</span>
            </span>
            <span v-if="result.badge" class="badge badge-muted flex-shrink-0">{{ result.badge }}</span>
          </li>
        </ul>
      </template>

      <!-- Recent searches (shown when query is empty) -->
      <template v-if="!results?.length && !query && showRecentList.length">
        <div
          class="flex items-center justify-between px-3 py-1.5 text-xs"
          style="border-bottom: 1px solid var(--color-border); color: var(--color-muted);"
        >
          <span>{{ $t('search.recent') }}</span>
          <button
            type="button"
            class="hover:text-white transition-colors"
            style="color: var(--color-muted);"
            @mousedown.prevent
            @click="handleClearRecent"
          >{{ $t('common.clear') }}</button>
        </div>
        <ul :id="`${id}-list`" role="listbox" :aria-label="$t('search.recent')">
          <li
            v-for="(term, i) in showRecentList"
            :key="term"
            :id="`${id}-option-${i}`" role="option" :aria-selected="i === highlightIdx"
            :class="{ 'bg-[var(--color-surface)]': i === highlightIdx }"
            class="flex items-center gap-2 px-3 py-2 cursor-pointer text-sm transition-colors hover:bg-[var(--color-surface)]"
            style="color: var(--color-muted);"
            @mousedown.prevent
            @click="selectRecent(term)"
          >
            <MdiHistory class="w-3.5 h-3.5 flex-shrink-0" />
            {{ term }}
          </li>
        </ul>
      </template>

      <!-- Empty state (query active but no results) -->
      <div
        v-if="query && results && !results.length && !loading"
        class="px-3 py-3 text-sm text-center"
        style="color: var(--color-muted);"
      >
        {{ $t('search.noResultsFor', { query }) }}
      </div>
    </div>

    <!-- Camera view -->
    <div
      v-if="cameraMode && cameraOpen"
      class="mt-2 rounded-lg overflow-hidden relative"
      style="border: 1px solid var(--color-border);"
    >
      <video ref="videoEl" class="w-full aspect-video bg-black" autoplay playsinline muted />
      <div
        v-if="!cameraSupported"
        class="absolute inset-0 flex items-center justify-center text-sm text-center px-4"
        style="background: rgba(0,0,0,0.8); color: var(--color-muted);"
      >
        {{ $t('search.cameraNotSupported') }}
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick, useId } from 'vue'
import MdiMagnify from '~icons/mdi/magnify'
import MdiClose from '~icons/mdi/close'
import MdiLoading from '~icons/mdi/loading'
import MdiQrcodeScan from '~icons/mdi/qrcode-scan'
import MdiHistory from '~icons/mdi/history'
import { useRecentSearches } from '@/composables/useRecentSearches.js'

const props = defineProps({
  label:        { type: String, default: '' },
  modelValue:   { type: String, default: '' },
  placeholder:  { type: String, default: 'Search…' },
  // null = pure filter mode (no dropdown rendered)
  // [] or populated = dropdown mode
  results:      { type: Array, default: null },
  loading:      { type: Boolean, default: false },
  error:        { type: String, default: null },
  debounce:     { type: Number, default: 300 },
  minLength:    { type: Number, default: 0 },
  scanMode:     { type: Boolean, default: false },
  cameraMode:   { type: Boolean, default: false },
  searchKey:    { type: String, default: null },
  // Milliseconds to suppress re-emit of the same barcode value
  scanCooldown: { type: Number, default: 1500 },
})

const emit = defineEmits(['update:modelValue', 'search', 'select', 'scan', 'clear'])

const { getRecent, addRecent, clearAll } = useRecentSearches()

const id = useId()
const options = computed(() => props.results?.length ? props.results : !query.value ? showRecentList.value : [])

const inputEl   = ref(null)
const wrapperEl = ref(null)
const videoEl   = ref(null)

const query        = ref(props.modelValue)
const focused      = ref(false)
const highlightIdx = ref(-1)
const cameraOpen   = ref(false)
const cameraSupported = ref(false)

// Duplicate scan cooldown tracking
const lastScannedCode = ref('')
const lastScannedAt   = ref(0)

let debounceTimer = null
let cameraStream  = null
let cameraDetector = null
let scanAnimFrame = null

const showRecentList = computed(() =>
  props.searchKey ? getRecent(props.searchKey) : [],
)

const dropdownVisible = computed(() => {
  if (!focused.value || props.results === null) return false
  const hasResults = props.results.length > 0
  const hasRecent  = !query.value && showRecentList.value.length > 0
  const hasEmpty   = !!query.value && query.value.length >= Math.max(props.minLength, 1) && !props.loading
  return hasResults || hasRecent || hasEmpty
})

// Sync external modelValue → local query
watch(() => props.modelValue, v => {
  if (v !== query.value) query.value = v
})

// Auto-refocus after error clears in scan mode
watch(() => props.error, v => {
  if (!v && props.scanMode) nextTick(() => inputEl.value?.focus())
})

// ── Input handling ───────────────────────────────────────────────────────────

function onInput(e) {
  const v = e.target.value
  query.value = v
  emit('update:modelValue', v)
  highlightIdx.value = -1
  clearTimeout(debounceTimer)
  if (props.debounce === 0) {
    if (v.length >= props.minLength) emit('search', v)
    return
  }
  debounceTimer = setTimeout(() => {
    if (v.length >= props.minLength) emit('search', v)
  }, props.debounce)
}

function onFocus() {
  focused.value = true
}

function onBlur() {
  focused.value = false
}

function onKeydown(e) {
  if (dropdownVisible.value && e.key === 'Enter' && options.value[highlightIdx.value] !== undefined) {
    e.preventDefault()
    const option = options.value[highlightIdx.value]
    typeof option === 'string' ? selectRecent(option) : selectResult(option)
    return
  }
  if (props.scanMode && e.key === 'Enter') {
    e.preventDefault()
    commitScan(query.value.trim())
    return
  }

  if (e.key === 'Escape') {
    e.preventDefault()
    focused.value = false
    highlightIdx.value = -1
    return
  }
  if (!dropdownVisible.value && e.key === 'ArrowDown') focused.value = true
  if (!dropdownVisible.value) return

  if (e.key === 'ArrowDown') {
    e.preventDefault()
    highlightIdx.value = Math.min(highlightIdx.value + 1, options.value.length - 1)
  } else if (e.key === 'ArrowUp') {
    e.preventDefault()
    highlightIdx.value = Math.max(highlightIdx.value - 1, -1)
  }
}

function selectResult(result) {
  if (props.searchKey && query.value.trim()) addRecent(props.searchKey, query.value)
  emit('select', result)
  query.value = ''
  emit('update:modelValue', '')
  focused.value = false
  highlightIdx.value = -1
}

function selectRecent(term) {
  query.value = term
  emit('update:modelValue', term)
  emit('search', term)
  focused.value = true
  nextTick(() => inputEl.value?.focus())
}

function clear() {
  query.value = ''
  emit('update:modelValue', '')
  emit('clear')
  clearTimeout(debounceTimer)
  highlightIdx.value = -1
  nextTick(() => inputEl.value?.focus())
}

function handleClearRecent() {
  if (props.searchKey) clearAll(props.searchKey)
}

// ── Highlight helper (XSS-safe) ──────────────────────────────────────────────

function htmlEscape(str) {
  return String(str ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function highlightMatch(text) {
  if (!text) return ''
  const safe = htmlEscape(text)
  if (!query.value) return safe
  const escaped = query.value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  if (!escaped) return safe
  return safe.replace(new RegExp(`(${htmlEscape(escaped)})`, 'gi'), '<mark class="search-highlight">$1</mark>')
}

// ── HID scan ─────────────────────────────────────────────────────────────────

function commitScan(code) {
  if (!code) return
  const now = Date.now()
  if (code === lastScannedCode.value && now - lastScannedAt.value < props.scanCooldown) return
  lastScannedCode.value = code
  lastScannedAt.value   = now
  clearTimeout(debounceTimer)
  query.value = ''
  emit('update:modelValue', '')
  emit('scan', code)
}

function onGlobalKeydown(e) {
  if (!props.scanMode) return
  if (e.defaultPrevented || e.key === ' ' || document.activeElement?.closest(
    'input, textarea, select, button, a, [contenteditable], [role="dialog"], [role="button"], [role="checkbox"]',
  )) return
  if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
    inputEl.value?.focus()
  }
}

// ── Camera scan ───────────────────────────────────────────────────────────────

async function toggleCamera() {
  cameraOpen.value ? closeCamera() : await openCamera()
}

async function openCamera() {
  cameraOpen.value = true
  if (!cameraSupported.value) return
  try {
    cameraStream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } })
    await nextTick()
    if (videoEl.value) {
      videoEl.value.srcObject = cameraStream
      cameraDetector = new window.BarcodeDetector({
        formats: [
          'code_128', 'ean_13', 'ean_8', 'upc_a', 'upc_e',
          'code_39', 'code_93', 'itf', 'codabar',
          'qr_code', 'data_matrix', 'pdf417', 'aztec',
        ],
      })
      scanFrame()
    }
  } catch {
    cameraOpen.value = false
  }
}

function scanFrame() {
  scanAnimFrame = requestAnimationFrame(async () => {
    if (!cameraOpen.value || !videoEl.value) return
    if (videoEl.value.readyState >= 2) {
      try {
        const codes = await cameraDetector.detect(videoEl.value)
        if (codes.length > 0) {
          closeCamera()
          commitScan(codes[0].rawValue)
          return
        }
      } catch {}
    }
    if (cameraOpen.value) scanFrame()
  })
}

function closeCamera() {
  cameraOpen.value = false
  cancelAnimationFrame(scanAnimFrame)
  cameraStream?.getTracks().forEach(t => t.stop())
  cameraStream = null
}

// ── Lifecycle ────────────────────────────────────────────────────────────────

onMounted(() => {
  cameraSupported.value = typeof window.BarcodeDetector !== 'undefined'
  if (props.scanMode) {
    window.addEventListener('keydown', onGlobalKeydown)
    nextTick(() => inputEl.value?.focus())
  }
})

onUnmounted(() => {
  window.removeEventListener('keydown', onGlobalKeydown)
  closeCamera()
  clearTimeout(debounceTimer)
})

defineExpose({ focus: () => nextTick(() => inputEl.value?.focus()) })
</script>
